/**
 * Payments — payout onboarding, earnings, and the pricing preview.
 *
 * `previewPricing` exists so the pricing editor can show a real consent line while a
 * developer types, WITHOUT the frontend owning a money formatter. The server renders it
 * with the same function the consent card and the store badge use, which is the only way
 * those three can be guaranteed to agree.
 */
/**
 * ONE PAYOUT READ PER PAGE LOAD, not three. The shell badge, Home's fleet signals, the
 * Money page and an app's Price page each ask for the same account within a second of
 * each other. Identical asks for one owner share the request in flight, and its answer
 * for a few seconds after. `fresh: true` always goes to the server (it asks Stripe) and
 * its answer replaces the shared copy; onboarding and the dashboard link drop the copy,
 * since both are a trip to Stripe that can change the account.
 */
const PAYOUT_TTL_MS = 5000;
const payoutCache = new Map(); // owner key → { at, pending, fresh, promise }

/**
 * WHO ELSE WANTS TO KNOW. The shell's Money badge reads the account on focus and route
 * changes, but the Money page's fresh read (the one that sees Stripe's new state after
 * onboarding) never reached it, so the badge stayed lit. Every answer that lands — fresh
 * or shared — and every invalidation is told to these listeners.
 *
 *   { groupId, response }   an answer for that owner (groupId null = personal)
 *   { invalidated: true }   the shared copy was dropped; ask again if you care
 *
 * An answer to an older request that lands after a newer one for the same owner is not
 * announced: it would put back the state the newer answer replaced.
 */
const payoutListeners = new Set();
let payoutSeq = 0;
const payoutLanded = new Map(); // owner key → seq of the newest request answered

function announcePayout(event) {
  payoutListeners.forEach((listener) => {
    try { listener(event); } catch { /* one listener's bug is not every listener's */ }
  });
}

function dropPayoutCache() {
  payoutCache.clear();
  announcePayout({ invalidated: true });
}

export default {
  /**
   * This developer's (or a group's) payout account. The server serves a copy synced
   * within the last minute; `fresh` makes it ask Stripe now (the Money page does, so
   * someone back from onboarding sees the real state).
   */
  getPayoutAccount(groupId, { fresh = false } = {}) {
    const key = groupId || '';
    const hit = payoutCache.get(key);
    if (hit && (hit.pending || Date.now() - hit.at < PAYOUT_TTL_MS)) {
      // A plain read takes whatever is shared; a fresh one only another fresh one
      // still in flight, since anything else may be the server's older copy.
      if (!fresh || (hit.pending && hit.fresh)) return hit.promise;
    }
    const q = new URLSearchParams();
    if (groupId) q.set('group_id', groupId);
    if (fresh) q.set('fresh', '1');
    const qs = q.toString();
    const entry = { at: Date.now(), pending: true, fresh };
    const seq = ++payoutSeq;
    entry.promise = this.request(`/billing/payout-account${qs ? `?${qs}` : ''}`).then(
      (res) => {
        entry.pending = false;
        entry.at = Date.now();
        if (seq > (payoutLanded.get(key) || 0)) {
          payoutLanded.set(key, seq);
          announcePayout({ groupId: groupId || null, response: res });
        }
        return res;
      },
      (err) => {
        // A failure is not shared: the next ask tries again.
        if (payoutCache.get(key) === entry) payoutCache.delete(key);
        throw err;
      },
    );
    payoutCache.set(key, entry);
    return entry.promise;
  },

  /** Forget the shared payout account, so the next read asks the server. */
  invalidatePayoutAccount() {
    dropPayoutCache();
  },

  /**
   * Hear every payout-account answer and invalidation (see `payoutListeners`).
   * Returns the unsubscribe.
   */
  onPayoutAccount(listener) {
    payoutListeners.add(listener);
    return () => { payoutListeners.delete(listener); };
  },

  /**
   * Start or RESUME Stripe onboarding. Idempotent: called again for an existing
   * account it issues a fresh link for the same one, because Stripe expires
   * onboarding links and "resume where I left off" is the common case.
   */
  async startPayoutOnboarding(body = {}) {
    dropPayoutCache();
    return this.request('/billing/payout-account', { method: 'POST', body });
  },

  /** A one-time link into the developer's own Stripe Express dashboard. */
  async payoutDashboardLink(body = {}) {
    dropPayoutCache();
    return this.request('/billing/payout-account/dashboard', { method: 'POST', body });
  },

  /** What an app has earned — per currency, gross and net, from frozen agreed prices. */
  async getEarnings(appId) {
    return this.request(`/third-party/${appId}/earnings`);
  },

  /**
   * Everything this developer earns, across every app they own.
   *
   * Two halves that deliberately disagree: `expected_by_currency` is one billing period
   * at the prices people agreed to, `collected_by_currency` is what Stripe has actually
   * charged. Presenting either alone as "revenue" would be wrong in a way nobody could
   * see from the number.
   */
  async getDeveloperEarnings(groupId) {
    const q = groupId ? `?group_id=${encodeURIComponent(groupId)}` : '';
    return this.request(`/billing/earnings${q}`);
  },
};
